'use client'

import { useEffect } from 'react'

import { type Filter } from '@rewardops/api'
import { useFilter } from '@rewardops/ui'

import { getSortOptionByKey } from '~/utils/filter-utils'
import { usePaginatedItems } from '~/components/hooks/use-paginated-items'

interface UseFilteredItemsProps {
  categoryCode?: string | null
  isPromotion?: boolean
  promotionLabel?: string
  setItemSearchState?:
    | ((prevState: {
        totalItems: number | undefined
        loading: boolean
      }) => void)
    | null
}

/**
 * `useFilteredItems` is a wrapper around `usePaginatedItems` that integrates
 * additional filtering logic based on category, search query, promotion, and locale.
 */
export const useFilteredItems = ({
  categoryCode,
  isPromotion,
  promotionLabel,
  setItemSearchState,
}: UseFilteredItemsProps) => {
  const {
    searchQuery,
    subCategory,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    sort,
    selectedBrands,
    selectedCategories,
    minPrice,
    maxPrice,
    onlyItemsWithinBalance,
  } = useFilter()

  const filter: Filter = {
    categoryCode: subCategory ? subCategory : categoryCode,
    promotionLabel: promotionLabel,
    brands: selectedBrands,
    categories: selectedCategories,
    minPrice,
    maxPrice,
    onlyItemsWithinBalance,
    sort: getSortOptionByKey(sort),
    ...(isPromotion != null ? { isPromotion } : {}),
    ...(searchQuery != null ? { q: searchQuery } : {}),
    isAllItems: !isPromotion && Boolean(promotionLabel),
  }

  // Reset to first page only when relevant filters change (not on page change)
  useEffect(() => {
    // Only reset to page 1 if not already on page 1
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [
    // Dependencies that should trigger a page reset
    subCategory,
    JSON.stringify(selectedBrands),
    JSON.stringify(selectedCategories),
    sort,
    isPromotion,
    searchQuery,
    minPrice,
    maxPrice,
    onlyItemsWithinBalance,
  ])

  const { loading, error, items, totalItems } = usePaginatedItems(
    filter,
    currentPage,
    itemsPerPage,
  )

  useEffect(() => {
    if (setItemSearchState) {
      setItemSearchState({ loading, totalItems })
    }
  }, [loading, items, setItemSearchState])

  return { loading, error, items, totalItems, itemsPerPage }
}
